/**
 * The icon every nav path renders with. One entry per `to:` in shellConfig.tsx.
 *
 * Why this is its own config rather than a map inside AppNav: the icon set is
 * nav DATA, and it has to stay in step with shellConfig's nav entries. When the
 * two lived apart, adding sub-page navs for Payroll/eSign/AI Expense Capture
 * (spec module-subpage-navs) added 13 paths and no icons, and every one of them
 * silently fell through to the generic FileText fallback — eSign rendered five
 * identical rows. Nothing failed; the sidebar just stopped carrying
 * information. scripts/check_nav_icons.py now fails the build on that.
 *
 * Three rules the set is built on:
 *
 *  1. One icon per CONCEPT, reused across modules. Module-level Settings is
 *     always SlidersHorizontal; Analytics/Insights is always TrendingUp. The
 *     same idea should not change shape depending on which module you are in.
 *  2. No two links visible AT THE SAME TIME may share an icon. The sidebar is
 *     scoped to one module plus Core-pinned plus System (see scopeNavToModule),
 *     so paths in different modules never co-occur and may reuse a glyph.
 *  3. Module vs Core duplicates must differ. A module sidebar shows two rows
 *     labelled "Dashboard" (the module's and Core's) and two labelled
 *     "Settings" (the module's and System's). Same label + same icon would make
 *     them genuinely indistinguishable, so the module's Settings is
 *     SlidersHorizontal against System's Settings cog.
 *
 * Lucide names variants with a numeric suffix (Building2, Settings2) rather
 * than a descriptive one — the number carries no meaning, it is just the second
 * drawing of that noun.
 */
import {
  FileSignature,
  Activity, ArrowLeftRight, Banknote, BarChart3, BookOpen, BookText, Bookmark,
  Briefcase, Building2, CalendarCheck, CalendarClock, CalendarDays, CalendarRange,
  ClipboardCheck, Clock, DoorOpen, Download, FileCheck, FileMinus, FileSpreadsheet,
  FileStack, FileText, Inbox, Landmark, Laptop, Layers, LayoutDashboard,
  LayoutTemplate, LifeBuoy, MapPin, Megaphone, MessageSquare, Network, NotebookPen,
  Package, PenLine, Receipt, ReceiptText, ScanFace, ScanLine, ScrollText, Send,
  Settings, Shield, SlidersHorizontal, Store, Target, Timer,
  TrendingUp, Upload, UserPlus, UserSearch, Users, Wallet,
} from "lucide-react";
import React from "react";

export type NavIcon = React.ComponentType<{ size?: number }>;

/** Rendered when a path has no entry. Reaching this is a bug — see the guard. */
export const FALLBACK_ICON: NavIcon = FileText;

export const NAV_ICONS: Record<string, NavIcon> = {
  // ── Core ───────────────────────────────────────────────────────────────────
  "/":                  LayoutDashboard,
  "/announcements":     Megaphone,
  "/my-leaves":         CalendarDays,
  // Approving others' leave is a different job from requesting your own, and HR
  // sees both rows at once — CalendarDays for both (as it was) made the pair
  // scan as one repeated item.
  "/leaves":            CalendarCheck,
  "/employees":         Users,
  "/attendance":        Clock,
  "/timesheet":         Timer,
  "/people-settings":   SlidersHorizontal,
  "/my-slips":          Receipt,
  "/expenses":          Wallet,
  "/assets":            Laptop,
  "/letters":           ScrollText,
  "/helpdesk":          LifeBuoy,
  "/referrals":         UserPlus,
  "/pulse":             Activity,
  "/policies":          BookText,
  "/exit":              DoorOpen,
  "/people-reports":    BarChart3,
  "/recruitment-reports": BarChart3,
  "/recruitment-reports/time-to-hire": BarChart3,
  "/recruitment-reports/sources": BarChart3,
  "/recruitment-reports/interviews": BarChart3,
  "/recruitment-reports/offers": BarChart3,
  "/recruitment-reports/pipeline": BarChart3,
  "/recruitment-reports/screening": BarChart3,
  "/people-reports/movement": BarChart3,
  "/people-reports/attrition": BarChart3,
  "/people-reports/org": BarChart3,
  "/people-reports/leave": BarChart3,
  "/people-reports/liability": BarChart3,
  "/people-reports/absence": BarChart3,
  "/payroll/reports":   BarChart3,
  "/org-chart":         Network,

  // ── System (visible inside every module, so these must stay unique) ────────
  "/signature-register": FileSignature,
  "/settings":          Settings,
  "/workspace":         Building2,
  "/security":          Shield,

  // ── Module dashboards ─────────────────────────────────────────────────────
  // All six share LayoutDashboard with Core's "/": they are all dashboards, and
  // pretending otherwise to dodge the duplicate would misdescribe them. The
  // module badge above and the section header between them do the separating.
  "/recruitment":       LayoutDashboard,
  "/finance":           LayoutDashboard,
  "/payroll-home":      LayoutDashboard,
  "/performance-home":  LayoutDashboard,
  "/esign-home":        LayoutDashboard,
  "/expenses-home":     LayoutDashboard,

  // ── Recruitment ───────────────────────────────────────────────────────────
  "/jobs":                   Briefcase,
  "/candidates":             UserSearch,
  "/talent-pool":            Bookmark,
  "/offers":                 FileCheck,
  "/interview-hub":          CalendarClock,
  "/screening":              ScanFace,
  "/recruiter-preferences":  SlidersHorizontal,

  // ── Finance ───────────────────────────────────────────────────────────────
  // Seven near-identical document types. They cannot all be a page-with-lines,
  // so each takes the glyph of what it DOES: money in, money out, tax, revision.
  "/quotations":                FileSpreadsheet,
  "/invoices":                  ReceiptText,
  "/billing-notes":             NotebookPen,
  "/tax-invoices":              Landmark,
  "/receipts":                  Receipt,
  "/debit-credit-notes":        FileMinus,
  "/sales-credit-debit-notes":  ArrowLeftRight,
  "/vendors":                   Store,
  "/vendor-bills":              Inbox,
  "/vendor-bills/bulk-upload":  Upload,
  "/products":                  Package,

  // ── Payroll ───────────────────────────────────────────────────────────────
  "/payroll":           Banknote,
  "/payroll/structure": Layers,   // pay built from stacked components
  "/payroll/settings":  SlidersHorizontal,
  "/payroll/exports":   Download,

  // ── eSign ─────────────────────────────────────────────────────────────────
  "/esign":            Send,      // "Send for Signature" — the outbound action
  "/esign/templates":  LayoutTemplate,
  "/esign/sent":       FileStack,
  "/esign/to-sign":    PenLine,   // matches EsignDashboardPage's signing glyph
  // Core signing inbox — same glyph as the eSign tab it replaces for signers,
  // because to the user it is the same task.
  "/to-sign":          PenLine,

  // ── AI Expense Capture ────────────────────────────────────────────────────
  "/ai-expense-capture":           ScanLine,
  "/ai-expense-capture/ledger":    BookOpen,
  "/ai-expense-capture/branches":  MapPin,   // branches are places, not org units
  "/ai-expense-capture/insights":  TrendingUp,
  "/ai-expense-capture/settings":  SlidersHorizontal,

  // ── Performance ───────────────────────────────────────────────────────────
  "/performance":            ClipboardCheck,
  "/performance/goals":      Target,
  "/performance/check-ins":  MessageSquare,
  "/performance/cycles":     CalendarRange,
  "/performance/analytics":  TrendingUp,
};

/** The icon for a nav path. Falls back rather than throwing — a missing icon
 *  must never blank the sidebar at runtime; the build-time guard is where it
 *  gets caught. */
export function iconFor(path: string): NavIcon {
  return NAV_ICONS[path] ?? FALLBACK_ICON;
}
